import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp } from "@/components/Reveal";
import Seo from "@/components/Seo";

const CONTACT_EMAIL = "hello@nanda-sa.com";

// Sujets alignés sur les 4 univers de la marque
const TOPICS = [
 { value: "Energie", label: { fr: "Énergie · Quincaillerie · Soudure (NANDA SÁ PRO)", en: "Energy · Hardware · Welding (NANDA SÁ PRO)", pt: "Energia · Ferragens · Soldadura (NANDA SÁ PRO)" } },
 { value: "Cosmetiques", label: { fr: "Cosmétiques", en: "Cosmetics", pt: "Cosméticos" } },
 { value: "Terroirs", label: { fr: "Produits & Terroirs", en: "Products & Terroirs", pt: "Produtos & Terroirs" } },
 { value: "Academie", label: { fr: "Académie", en: "Academy", pt: "Academia" } },
 { value: "Autre", label: { fr: "Autre", en: "Other", pt: "Outro" } },
];

export default function Contact() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);
 const [params] = useSearchParams();
 const preSubject = params.get("subject") || "";

 // Si un sujet pré-rempli n'est pas un univers (ex. "Academie-programme"), on le conserve comme option
 const topicValues = TOPICS.map((t) => t.value);
 const extraSubject = preSubject && !topicValues.includes(preSubject) ? preSubject : null;

 const [form, setForm] = useState({ name: "", email: "", subject: preSubject, message: "" });
 const [submitting, setSubmitting] = useState(false);
 const [submitted, setSubmitted] = useState(false);

 const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

 const onSubmit = (e) => {
 e.preventDefault();
 if (!form.name || !form.email || !form.message) {
 toast.error(l("Champs requis manquants", "Required fields missing", "Campos obrigatórios em falta"));
 return;
 }
 setSubmitting(true);

 const topic = TOPICS.find((t) => t.value === form.subject);
 const subjectLabel = topic ? topic.label[locale] || topic.label.fr : form.subject;
 const subjectLine = `[NANDA SÁ] ${subjectLabel || l("Contact", "Contact", "Contacto")}`;
 const bodyLines = [
 `${l("Nom", "Name", "Nome")}: ${form.name}`,
 `Email: ${form.email}`,
 subjectLabel ? `${l("Sujet", "Subject", "Assunto")}: ${subjectLabel}` : null,
 "",
 form.message,
 ].filter(Boolean);

 const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
 window.location.href = mailto;

 setTimeout(() => {
 setSubmitted(true);
 toast.success(l("Votre client mail vient de s'ouvrir. Envoyez le message pour finaliser.", "Your mail client just opened. Send the message to complete.", "O seu cliente de e-mail acaba de abrir. Envie a mensagem para concluir."));
 setSubmitting(false);
 }, 400);
 };

 return (
 <div data-testid="page-contact" className="bg-white">
 <Seo path="/contact" />

 {/* En-tête */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-16 md:pb-24">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-8">Contact</div>
 </FadeUp>
 <FadeUp delay={0.1}>
 <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance">
 {l("Écrivez-nous.", "Write to us.", "Escreva-nos.")}
 </h1>
 </FadeUp>
 <FadeUp delay={0.2}>
 <p className="mt-8 max-w-xl text-nanda-inkSoft text-lg font-light leading-relaxed">
 {l("Une question, un projet, une commande ? Choisissez un univers, laissez un message. Nous répondons personnellement.", "A question, a project, an order? Choose a universe, leave a message. We answer personally.", "Uma questão, um projecto, uma encomenda? Escolha um universo, deixe uma mensagem. Respondemos pessoalmente.")}
 </p>
 </FadeUp>
 </div>
 </section>

 <section className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-20">
 {/* Coordonnées directes */}
 <div className="md:col-span-5">
 <FadeUp>
 <div className="space-y-12">
 <div>
 <div className="label-overline text-nanda-muted mb-6 inline-flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} /> {l("Nos implantations", "Our locations", "As nossas implantações")}</div>
 <div className="space-y-10">
 <div data-testid="contact-implant-senegal">
 <div className="label-overline text-nanda-ink mb-3">Sénégal</div>
 <div className="text-nanda-inkSoft text-sm">Ziguinchor, Sénégal</div>
 <a href="https://wa.me/221710090410" target="_blank" rel="noopener noreferrer" data-testid="contact-phone-sn" className="mt-2 inline-flex items-center gap-2 text-nanda-ink text-xl font-light hover:text-nanda-terracotta transition-colors">
 <MessageCircle size={15} strokeWidth={1.5} className="text-nanda-muted" /> +221 71 009 04 10
 </a>
 </div>
 <div data-testid="contact-implant-guinee-bissau">
 <div className="label-overline text-nanda-ink mb-3">Guinée-Bissau</div>
 <div className="text-nanda-inkSoft text-sm">Canchungo, Région de Cacheu, Guinée-Bissau</div>
 <a href="https://wa.me/245956828351" target="_blank" rel="noopener noreferrer" data-testid="contact-phone-gw" className="mt-2 inline-flex items-center gap-2 text-nanda-ink text-xl font-light hover:text-nanda-terracotta transition-colors">
 <MessageCircle size={15} strokeWidth={1.5} className="text-nanda-muted" /> +245 956 828 351
 </a>
 </div>
 </div>
 </div>
 <div>
 <div className="label-overline text-nanda-muted mb-4 inline-flex items-center gap-2"><Mail size={14} strokeWidth={1.5} /> E-mail</div>
 <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="block text-nanda-ink text-xl font-light hover:text-nanda-terracotta transition-colors">
 {CONTACT_EMAIL}
 </a>
 </div>
 </div>
 </FadeUp>
 </div>

 {/* Formulaire */}
 <FadeUp delay={0.1} className="md:col-span-7">
 {submitted ? (
 <div data-testid="contact-success" className="border border-black/[0.06] rounded-3xl p-10 md:p-14 bg-nanda-bgAlt">
 <div className="label-overline text-nanda-terracotta">✓</div>
 <p className="mt-6 font-sans font-light text-2xl md:text-3xl leading-snug text-nanda-ink">{l("Votre client mail vient de s'ouvrir. Envoyez le message pour finaliser.", "Your mail client just opened. Send the message to complete.", "O seu cliente de e-mail acaba de abrir. Envie a mensagem para concluir.")}</p>
 <a href={`mailto:${CONTACT_EMAIL}`} className="mt-8 inline-flex items-center gap-3 text-nanda-ink hover:text-nanda-terracotta transition-colors label-overline border-b border-nanda-line pb-1" data-testid="contact-mailto-fallback">
 <Mail size={14} strokeWidth={1.5} /> {CONTACT_EMAIL}
 </a>
 </div>
 ) : (
 <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-9" noValidate>
 <Field id="name" label={l("Nom", "Name", "Nome")} value={form.name} onChange={update("name")} required testId="contact-name" />
 <Field id="email" type="email" label="Email" value={form.email} onChange={update("email")} required testId="contact-email" />

 <div>
 <label htmlFor="subject" className="label-overline text-nanda-muted block mb-3">{l("Sujet", "Subject", "Assunto")}</label>
 <select id="subject" value={form.subject} onChange={update("subject")} data-testid="contact-subject" className="w-full bg-transparent border-b border-nanda-line py-3 text-nanda-ink font-light outline-none focus:border-nanda-ink transition-colors">
 <option value="">{l("Choisir un univers", "Choose a universe", "Escolher um universo")}</option>
 {TOPICS.map((t) => (
 <option key={t.value} value={t.value} data-testid={`contact-topic-${t.value.toLowerCase()}`}>{t.label[locale] || t.label.fr}</option>
 ))}
 {extraSubject && <option value={extraSubject}>{extraSubject}</option>}
 </select>
 </div>

 <div>
 <label htmlFor="message" className="label-overline text-nanda-muted block mb-3">
 {l("Message", "Message", "Mensagem")}<span className="text-nanda-terracotta ml-1">*</span>
 </label>
 <textarea id="message" rows={5} value={form.message} onChange={update("message")} required data-testid="contact-message" className="w-full bg-transparent border-b border-nanda-line py-3 text-nanda-ink font-light outline-none focus:border-nanda-ink transition-colors resize-none" />
 </div>

 <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300 disabled:opacity-50">
 <Mail size={15} strokeWidth={1.5} />
 {submitting ? l("Ouverture…", "Opening…", "A abrir…") : l("Envoyer", "Send", "Enviar")}
 </button>
 <p className="text-xs text-nanda-muted">{l("Votre client mail s'ouvrira avec le message pré-rempli vers", "Your email client will open with the pre-filled message to", "O seu cliente de e-mail abrirá com a mensagem pré-preenchida para")} <span className="text-nanda-ink">{CONTACT_EMAIL}</span>.</p>
 </form>
 )}
 </FadeUp>
 </section>
 </div>
 );
}

const Field = ({ id, label, value, onChange, type = "text", required, testId }) => (
 <div>
 <label htmlFor={id} className="label-overline text-nanda-muted block mb-3">
 {label}{required && <span className="text-nanda-terracotta ml-1">*</span>}
 </label>
 <input id={id} type={type} value={value} onChange={onChange} required={required} data-testid={testId} className="w-full bg-transparent border-b border-nanda-line py-3 text-nanda-ink font-light outline-none focus:border-nanda-ink transition-colors" />
 </div>
);
